import { computed, type ComputedRef, type Ref } from 'vue'
import { useFilters } from '@/modules/shared/composables/useFilters'
import type { Transaction } from '@/modules/investing/types'
import { getDisplayStatus, STATUS_FILTER_OPTIONS } from '../utils/transaction-status'

type DateRangeValue = { start: string | null; end: string | null }

export const normalize = (v: any): string => {
  if (v == null) return ''
  return String(v).toLowerCase().replace(/[$,]/g, '').trim()
}

const typeOptions = [
  { value: 'capital_call', text: 'Capital Call' },
  { value: 'scheduled_call', text: 'Scheduled Call' },
  { value: 'cash_distribution', text: 'Distribution to Investors' },
  { value: 'portfolio_distribution', text: 'Distribution from Portfolio' },
  { value: 'asset_funds', text: 'Asset Funds' },
  { value: 'loss_investment', text: 'Realized Loss' },
  { value: 'management_fee', text: 'Management Fees' },
  { value: 'other_fee', text: 'Other Fees' },
  { value: 'operating_income', text: 'Operating Income' },
  { value: 'operating_expense', text: 'Operating Expense' },
  { value: 'recycled_funds', text: 'Recycled Funds' },
  { value: 'syndication_fee', text: 'Syndication Cost' },
  { value: 'interest_allocation_received', text: 'Catch-up Interest Received' },
  { value: 'interest_allocation_paid', text: 'Catch-up Interest Paid' },
]

export function useTransactionFilters(transactions: ComputedRef<any[]> | Ref<any[]>, entityId: string) {
  const isCurrentEntity = (t: any) => String(t.investor_type) === 'InvestorSet' && String(t.investor_id) === entityId

  const investorOptions = computed(() => {
    const uniqueInvestors = new Map<string, string>()

    transactions.value.forEach((item) => {
      if (!item.investor_id || !item.investor_type) return
      if (isCurrentEntity(item)) return
      const key = `${item.investor_type}:${item.investor_id}`
      if (!uniqueInvestors.has(key)) {
        uniqueInvestors.set(key, item.investor_name)
      }
    })

    return Array.from(uniqueInvestors.entries())
      .map(([value, text]) => ({ value, text }))
      .sort((a, b) => a.text.toLowerCase().localeCompare(b.text.toLowerCase()))
  })

  const filterConfigs = [
    {
      key: 'type',
      label: 'Type',
      type: 'multi' as const,
      options: typeOptions,
      assumeAllSelected: true,
      value: [],
      apply: (item: any, val: string[]) => {
        const v = Array.isArray(val) ? val : []
        if (v.length === 0) return false
        return v.includes(item.display_type)
      },
    },
    {
      key: 'status',
      label: 'Status',
      type: 'multi' as const,
      options: STATUS_FILTER_OPTIONS,
      assumeAllSelected: true,
      value: [],
      apply: (item: any, val: string[]) => {
        const v = Array.isArray(val) ? val : []
        if (v.length === 0) return false
        return v.includes(getDisplayStatus(item))
      },
    },
    {
      key: 'investor',
      label: 'Investor',
      type: 'multi' as const,
      options: investorOptions,
      assumeAllSelected: true,
      value: [],
      apply: (item: Transaction, val: string[]) => {
        if (!item.investor_id || !item.investor_type || isCurrentEntity(item)) return true
        const v = Array.isArray(val) ? val : []
        if (v.length === 0) return false
        const key = `${item.investor_type}:${item.investor_id}`
        return v.includes(key)
      },
    },
    {
      key: 'dateRange',
      label: 'Date',
      type: 'range' as const,
      value: { start: null, end: null } as DateRangeValue,
      apply: (item: Transaction, val?: DateRangeValue | null) => {
        const start = val?.start
        const end = val?.end
        const itemDate = String(item.date).slice(0, 10)

        if (start && itemDate < start) return false
        if (end && itemDate > end) return false
        return true
      },
    },
    {
      key: 'search',
      label: 'Search',
      value: '',
      apply: (item: any, val: string) => {
        const query = normalize(val)
        if (!query) return true
        return Object.values(item).some((v) => normalize(v).includes(query))
      },
    },
  ]

  const { filters, proxies, apply } = useFilters(filterConfigs)

  const typeFilter = proxies.type as Ref<string[]>
  const statusFilter = proxies.status as Ref<string[]>
  const investorFilter = proxies.investor as Ref<string[]>
  const dateRangeFilter = proxies.dateRange as Ref<DateRangeValue>
  const searchFilter = proxies.search as Ref<string>

  const startDateFilter = computed({
    get: () => dateRangeFilter.value.start,
    set: (value: string | null) => (dateRangeFilter.value = { ...dateRangeFilter.value, start: value }),
  })
  const endDateFilter = computed({
    get: () => dateRangeFilter.value.end,
    set: (value: string | null) => (dateRangeFilter.value = { ...dateRangeFilter.value, end: value }),
  })

  const rangeAccessors = {
    dateRange: {
      start: startDateFilter,
      end: endDateFilter,
    },
  }

  const filtersDropdownConfig = computed(() =>
    filters
      .filter((filter) => filter.key !== 'search')
      .map((filter) => {
        if (filter.type === 'range') {
          return {
            key: filter.key,
            label: filter.label,
            type: 'range' as const,
            range: rangeAccessors[filter.key as keyof typeof rangeAccessors],
          }
        }

        return {
          key: filter.key,
          label: filter.label,
          type: 'multi' as const,
          options: (filter as any).options,
          model: proxies[filter.key] as Ref<string[]>,
          assumeAllSelected: (filter as any).assumeAllSelected,
        }
      }),
  )

  const filteredItems = computed(() => apply(transactions.value))
  const isFilteredEmpty = computed(() => filteredItems.value.length === 0 && transactions.value.length > 0)

  const hasActiveFilters = computed(() => {
    if (dateRangeFilter.value.start || dateRangeFilter.value.end) return true
    if (searchFilter.value && searchFilter.value.length > 0) return true

    const typeNarrowed = typeFilter.value && typeFilter.value.length > 0 && typeFilter.value.length < typeOptions.length
    const statusNarrowed =
      statusFilter.value && statusFilter.value.length > 0 && statusFilter.value.length < STATUS_FILTER_OPTIONS.length
    const investorNarrowed =
      investorFilter.value &&
      investorFilter.value.length > 0 &&
      investorFilter.value.length < investorOptions.value.length

    return Boolean(typeNarrowed || statusNarrowed || investorNarrowed)
  })

  const clearAll = () => {
    typeFilter.value = []
    statusFilter.value = []
    investorFilter.value = []
    dateRangeFilter.value = { start: null, end: null }
    searchFilter.value = ''
  }
  const tableEmptyTitle = computed(() =>
    isFilteredEmpty.value ? 'No results match your filters' : 'No data to display',
  )
  const tableEmptyMessage = computed(() =>
    isFilteredEmpty.value
      ? 'Try widening your filters or clearing your search to see transactions.'
      : 'Data will appear here once transactions are available.',
  )

  return {
    typeFilter,
    statusFilter,
    investorFilter,
    searchFilter,
    startDateFilter,
    endDateFilter,
    investorOptions,
    filtersDropdownConfig,
    filteredItems,
    isFilteredEmpty,
    hasActiveFilters,
    clearAll,
    tableEmptyTitle,
    tableEmptyMessage,
  }
}
